import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiService } from '@/services/api'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Plus, FileText, Edit, Trash2, RefreshCw, Copy } from 'lucide-react'
import { toast } from 'sonner'
import { useConfirm } from '@/hooks/useConfirm'
import { useDrawer } from '@/components/ui/drawer'
import { BlogTemplateFormDrawer } from '@/components/blog/BlogTemplateFormDrawer'

interface BlogTemplate {
  id: string
  name: string
  description?: string
  title_template?: string
  content_template: string
  excerpt_template?: string
  created_at: string
  updated_at: string
  categories: Array<{ id: string; name: string; color?: string }>
  tags: Array<{ id: string; name: string }>
}

export default function BlogTemplates() {
  const { openDrawer, closeDrawer } = useDrawer()
  const queryClient = useQueryClient()
  const { confirm } = useConfirm()

  // Fetch templates
  const {
    data: templates,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['blog-templates'],
    queryFn: async () => {
      const response = await apiService.blog.templates.list()
      return response as BlogTemplate[]
    },
  })

  // Delete template mutation
  const deleteMutation = useMutation({
    mutationFn: apiService.blog.templates.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['blog-templates'] })
      toast.success('Template deleted successfully')
    },
    onError: (error: any) => {
      toast.error(`Failed to delete template: ${error.message}`)
    },
  })

  const handleCreateTemplate = () => {
    const drawerId = 'create-template'
    openDrawer({
      id: drawerId,
      content: (
        <BlogTemplateFormDrawer
          onClose={() => closeDrawer(drawerId)}
          onSuccess={() => {
            closeDrawer(drawerId)
            refetch()
          }}
        />
      ),
      title: 'Create Template',
      size: '4xl',
    })
  }

  const handleEditTemplate = (template: BlogTemplate) => {
    const drawerId = `edit-template-${template.id}`
    openDrawer({
      id: drawerId,
      content: (
        <BlogTemplateFormDrawer
          template={template}
          onClose={() => closeDrawer(drawerId)}
          onSuccess={() => {
            closeDrawer(drawerId)
            refetch()
          }}
        />
      ),
      title: 'Edit Template',
      size: '4xl',
    })
  }

  const handleDuplicateTemplate = async (template: BlogTemplate) => {
    try {
      await apiService.blog.templates.create({
        name: `${template.name} (Copy)`,
        description: template.description,
        title_template: template.title_template,
        content_template: template.content_template,
        excerpt_template: template.excerpt_template,
        category_ids: template.categories.map(c => c.id),
        tag_ids: template.tags.map(t => t.id),
      })
      queryClient.invalidateQueries({ queryKey: ['blog-templates'] })
      toast.success('Template duplicated successfully')
    } catch (error: any) {
      toast.error(`Failed to duplicate template: ${error.message}`)
    }
  }

  const handleDeleteTemplate = (template: BlogTemplate) => {
    confirm({
      title: 'Delete Template',
      message: `Are you sure you want to delete "${template.name}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'destructive',
      onConfirm: () => {
        deleteMutation.mutate(template.id)
      },
    })
  }

  if (error) {
    return (
      <div className="flex h-full items-center justify-center">
        <Card className="bg-card text-card-foreground border-border">
          <CardContent className="pt-6">
            <p className="text-destructive">Error loading templates: {error.message}</p>
            <Button onClick={() => refetch()} className="mt-4">
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="h-6 w-6 text-primary" />
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                Content Templates
              </h1>
              <p className="text-sm text-muted-foreground">
                Create and manage reusable blog post templates
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isLoading}
              className="border-border hover:bg-accent"
            >
              <RefreshCw
                className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
              />
              <span className="ml-2">Refresh</span>
            </Button>

            <Button
              size="sm"
              onClick={handleCreateTemplate}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" />
              <span className="ml-2">New Template</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="flex-1 overflow-y-auto p-6">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <div className="text-center">
              <RefreshCw className="mx-auto h-8 w-8 animate-spin text-primary" />
              <p className="mt-2 text-sm text-muted-foreground">Loading templates...</p>
            </div>
          </div>
        ) : templates && templates.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {templates.map((template) => (
              <Card
                key={template.id}
                className="bg-card text-card-foreground border-border hover:shadow-md transition-shadow cursor-pointer"
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{template.name}</CardTitle>
                      {template.description && (
                        <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
                          {template.description}
                        </p>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Categories & Tags */}
                  {(template.categories.length > 0 || template.tags.length > 0) && (
                    <div className="space-y-2">
                      {template.categories.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {template.categories.map((category) => (
                            <Badge
                              key={category.id}
                              variant="outline"
                              style={category.color ? { borderColor: category.color } : {}}
                              className="text-xs"
                            >
                              {category.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                      {template.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {template.tags.map((tag) => (
                            <Badge
                              key={tag.id}
                              variant="secondary"
                              className="text-xs"
                            >
                              {tag.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Template Details */}
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>
                      {template.title_template ? 'With title' : 'Content only'}
                    </span>
                    <span>
                      {new Date(template.created_at).toLocaleDateString()}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 border-t border-border pt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditTemplate(template)}
                      className="flex-1"
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDuplicateTemplate(template)}
                      className="flex-1"
                    >
                      <Copy className="h-3 w-3 mr-1" />
                      Duplicate
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteTemplate(template)}
                      className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="flex h-64 flex-col items-center justify-center">
            <FileText className="h-12 w-12 text-muted-foreground/50" />
            <p className="mt-4 text-lg font-medium text-foreground">No templates yet</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Create your first template to get started
            </p>
            <Button
              onClick={handleCreateTemplate}
              className="mt-4"
            >
              <Plus className="mr-2 h-4 w-4" />
              Create Template
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
